import { makeAutoObservable, runInAction } from 'mobx'
import { get } from 'lodash'
import StoreReferralCart from './StoreReferralCart'
import StoreReferralFormat from './StoreReferralFormat'
import storeReferralCheckout from './StoreReferralCheckout'
import storeFeelGreat from 'GlobalStores/FeelGreat/StoreFeelGreat'
import storeCountry from 'GlobalStores/Cores/County/StoreCountry'
import storeAuth from 'GlobalStores/User/StoreAuth'
import StoreFormat from 'GlobalStores/Format/StoreFormat'
import { FormatAPIs } from 'Services/FeelGreat/FormatAPIs'
import { GetFeelGreatProduct } from 'Services/FeelGreat/useFeelGreat'
import { ReferralUaeAPIs } from '../Services/ReferralUaeAPIs'
import { buildAddonCatalog, buildSessionItems, parseInvalidItemError, parseMessageV2, resolvePriceOption } from '../Helpers/addonHelpers'
import { checkoutErrorMessages } from '../Helpers/checkoutErrors'
import { translateKey } from 'GlobalStores/Cores/Translator/useTranslate'

export const DRAFT_ERRORS = {
    EMPTY_DRAFT: 'empty_draft'
}

export const COMMIT_RESULT = {
    SUCCESS: 'success',
    VALIDATION: 'validation',
    PUT_FAILED: 'put_failed',
    INVALID_ITEM_CODE: 'invalid_item_code',
    REFRESH_FAILED: 'refresh_failed'
}

// FE owns both strings; see Phase 9 dictionary list. The Errors block renders plain strings as they
// are, so these have to be handed over translated, never as raw keys.
export const COMMIT_ERROR_KEYS = {
    INVALID_ITEM_CODE: 'error_ufg_invalid_item_code',
    UPDATE_FAILED: 'update_cart_failed'
}

export const COMMIT_ERROR_FALLBACKS = {
    [COMMIT_ERROR_KEYS.INVALID_ITEM_CODE]: 'Sorry, one or more products are no longer available. Please remove them and update your cart.',
    [COMMIT_ERROR_KEYS.UPDATE_FAILED]: 'We could not update your cart. Please try again.'
}

export const commitErrorText = key => translateKey(key, { defaultDisplay: COMMIT_ERROR_FALLBACKS[key] })

class storeAddEditProduct {
    isOpen = false
    discardOpen = false
    committing = false
    syncError = false

    draft = {}
    committedSnapshot = {}
    draftProducts = {}
    unavailableCodes = []
    validationErrors = []
    addonsClearedByPackRemoval = false
    commitResult = null

    constructor() {
        makeAutoObservable(this)
    }

    PaymentOption() {
        return StoreReferralFormat.paymentOptionSelected
    }

    Catalog() {
        const published = get(storeFeelGreat.GetContent('additionalProductList'), 'additionalProductListFormat.items', [])
        return buildAddonCatalog(published, this.PaymentOption())
    }

    Open() {
        runInAction(() => {
            this.draft = {}
            this.draftProducts = {}

            StoreReferralCart.items.forEach(item => {
                this.draft[item.id] = item.qty
                this.draftProducts[item.id] = item
            })

            // the fresh publish wins on price/stock; only the draft qty comes from the cart
            this.Catalog().forEach(product => {
                this.draftProducts[product.id] = product
            })

            this.committedSnapshot = { ...this.draft }
            this.validationErrors = []
            this.addonsClearedByPackRemoval = false
            this.discardOpen = false
            this.isOpen = true
        })
    }

    TryClose() {
        if (this.IsDirty()) {
            this.discardOpen = true
            return
        }
        this.Close()
    }

    KeepEditing() {
        this.discardOpen = false
    }

    Close() {
        runInAction(() => {
            this.draft = { ...this.committedSnapshot }
            this.validationErrors = []
            this.addonsClearedByPackRemoval = false
            this.discardOpen = false
            this.isOpen = false
        })
    }

    ReopenWithDraft() {
        runInAction(() => {
            this.discardOpen = false
            this.isOpen = true
        })
    }

    IsDirty() {
        const draftKeys = Object.keys(this.draft)
        const snapshotKeys = Object.keys(this.committedSnapshot)

        if (draftKeys.length !== snapshotKeys.length) {
            return true
        }

        return draftKeys.some(key => this.draft[key] !== this.committedSnapshot[key])
    }

    Product(id) {
        return this.draftProducts[id]
    }

    IsAddon(id) {
        return get(this.Product(id), 'isAddon') === true
    }

    StockOf(id) {
        const qty = get(resolvePriceOption(this.Product(id), this.PaymentOption()), 'qty')
        return Number.isFinite(Number(qty)) ? Number(qty) : 0
    }

    PackLimit() {
        return StoreReferralCart.GetOverseasShippingLimit(storeReferralCheckout.GetCurrentAddressCountry())
    }

    MaxQtyOf(id) {
        const stock = this.StockOf(id)
        if (this.IsAddon(id)) {
            return stock
        }
        const limit = this.PackLimit()
        return limit ? Math.min(stock, limit) : stock
    }

    DraftRows() {
        // numeric-looking object keys iterate in numeric order, so role order is set explicitly
        const rows = Object.keys(this.draft)
            .filter(id => this.draftProducts[id])
            .map(id => ({ ...this.draftProducts[id], qty: this.draft[id] }))

        return [...rows.filter(row => row.isAddon !== true), ...rows.filter(row => row.isAddon === true)]
    }

    PackRows() {
        return this.DraftRows().filter(row => row.isAddon !== true)
    }

    AddonRows() {
        return this.DraftRows().filter(row => row.isAddon === true)
    }

    PackQty() {
        return this.PackRows().reduce((total, row) => total + (Number(row.qty) || 0), 0)
    }

    HasPack() {
        return this.PackQty() > 0
    }

    CanAddAddons() {
        return this.HasPack()
    }

    QtyOf(id) {
        return this.draft[id] ?? 0
    }

    IsInDraft(id) {
        return this.QtyOf(id) > 0
    }

    ItemCodeOf(id) {
        return get(resolvePriceOption(this.Product(id), this.PaymentOption()), 'item_code', id)
    }

    IsUnavailable(id) {
        return this.unavailableCodes.includes(`${id}`) || this.unavailableCodes.includes(`${this.ItemCodeOf(id)}`)
    }

    MarkUnavailable(codes) {
        runInAction(() => {
            const merged = new Set([...this.unavailableCodes, ...(Array.isArray(codes) ? codes : [])].map(code => `${code}`))
            this.unavailableCodes = [...merged]
        })
    }

    ClearUnavailable() {
        this.unavailableCodes = []
    }

    SetQty(id, qty) {
        if (!this.Product(id) || this.IsUnavailable(id)) {
            return
        }

        const requested = Number(qty)
        if (!Number.isFinite(requested)) {
            return
        }

        runInAction(() => {
            const capped = Math.max(0, Math.min(requested, this.MaxQtyOf(id)))

            if (this.IsAddon(id) && capped === 0) {
                delete this.draft[id]
            } else {
                this.draft[id] = capped
            }

            this.validationErrors = []
            this.EnforcePackRule()
        })
    }

    IncreaseQty(id) {
        if (this.IsAddon(id) && !this.CanAddAddons()) {
            return
        }
        this.SetQty(id, this.QtyOf(id) + 1)
    }

    // The cart must never be emptied from the modal (user, 08-06): a PUT with items:"[]" is accepted
    // by the session API but the following detail-v2 then fails on os-validation (400, "No items"),
    // which leaves the page in a state it cannot recover from. So the last pack unit is not removable:
    // its Remove button is hidden and a decrease on it does nothing.
    IsLastPackRow(id) {
        if (!this.Product(id) || this.IsAddon(id)) {
            return false
        }
        return this.PackRows().filter(row => `${row.id}` !== `${id}`).length === 0
    }

    CanRemoveRow(id) {
        return !this.IsLastPackRow(id)
    }

    CanDecreaseQty(id) {
        return !this.IsLastPackRow(id) || this.QtyOf(id) > 1
    }

    DecreaseQty(id) {
        if (!this.CanDecreaseQty(id)) {
            return
        }
        this.SetQty(id, this.QtyOf(id) - 1)
    }

    RemoveRow(id) {
        if (!this.CanRemoveRow(id)) {
            return
        }

        runInAction(() => {
            delete this.draft[id]
            this.validationErrors = []
            this.EnforcePackRule()
        })
    }

    AddFromCatalog(id) {
        if (!this.CanAddAddons() || this.IsUnavailable(id)) {
            return
        }

        const product = this.Product(id)
        if (!product || product.isAddon !== true) {
            return
        }

        this.SetQty(id, this.QtyOf(id) + 1)
    }

    EnforcePackRule() {
        if (this.HasPack()) {
            return
        }

        const addonIds = Object.keys(this.draft).filter(id => this.IsAddon(id))
        if (addonIds.length === 0) {
            return
        }

        addonIds.forEach(id => delete this.draft[id])
        this.addonsClearedByPackRemoval = true
    }

    ShowPackRemovalWarning() {
        return this.PackQty() === 0
    }

    IsEmptyDraft() {
        return this.DraftRows().every(row => (Number(row.qty) || 0) <= 0)
    }

    SessionItems() {
        return buildSessionItems(this.DraftRows().filter(row => (Number(row.qty) || 0) > 0), this.PaymentOption())
    }

    Validate() {
        const { items, errors } = this.SessionItems()
        const all = items.length === 0 && errors.length === 0 ? [DRAFT_ERRORS.EMPTY_DRAFT] : errors

        runInAction(() => {
            this.validationErrors = all
        })
        return all.length === 0
    }

    SetCommitting(value) {
        this.committing = value
    }

    SetSyncError(value) {
        this.syncError = value
    }

    async Commit() {
        if (!this.Validate()) {
            return this.FinishCommit(COMMIT_RESULT.VALIDATION)
        }

        const { items } = this.SessionItems()

        runInAction(() => {
            this.committing = true
            this.commitResult = null
            this.isOpen = false
            this.discardOpen = false
        })

        storeReferralCheckout.SetErrors([])

        try {
            const response = await FormatAPIs.putAddress(
                { uuid: storeReferralCheckout.GetUuid(), items: JSON.stringify(items) },
                storeCountry.Country3()
            )

            // a 2xx with success:false is still a failure — the session did not change
            if (get(response, 'data.success') !== true) {
                return await this.HandlePutFailure(response)
            }
        } catch (error) {
            return await this.HandlePutFailure(error)
        }

        StoreReferralCart.ReplaceItems(this.DraftRows().filter(row => (Number(row.qty) || 0) > 0))
        this.CommitAccepted()

        const refreshed = await this.RefreshTotals()
        return this.FinishCommit(refreshed ? COMMIT_RESULT.SUCCESS : COMMIT_RESULT.REFRESH_FAILED)
    }

    async HandlePutFailure(failure) {
        const { isInvalidItemCode, itemCodes } = parseInvalidItemError(failure)

        // The endpoint's own Message_v2 says which field it rejected; our two lines are only what we
        // fall back to when it said nothing we have copy for.
        const apiMessages = checkoutErrorMessages(parseMessageV2(failure), get(StoreFormat.GetDetailPage(), 'country'))

        storeReferralCheckout.SetErrors(
            apiMessages.length > 0 ? apiMessages : [commitErrorText(isInvalidItemCode ? COMMIT_ERROR_KEYS.INVALID_ITEM_CODE : COMMIT_ERROR_KEYS.UPDATE_FAILED)]
        )

        if (isInvalidItemCode) {
            this.MarkUnavailable(itemCodes)
            await this.RefreshPublish()
        }

        this.ReopenWithDraft()

        return this.FinishCommit(isInvalidItemCode ? COMMIT_RESULT.INVALID_ITEM_CODE : COMMIT_RESULT.PUT_FAILED)
    }

    async RefreshTotals() {
        try {
            const { data } = await ReferralUaeAPIs.getHotFormatV2(storeReferralCheckout.GetUuid(), storeCountry.Country3(), true)
            await StoreFormat.SetData(data)
            runInAction(() => (this.syncError = false))
            return true
        } catch (error) {
            console.error('AddEditProduct refresh failed', error)
            runInAction(() => (this.syncError = true))
            return false
        }
    }

    async RetryRefresh() {
        runInAction(() => (this.committing = true))
        const refreshed = await this.RefreshTotals()
        runInAction(() => (this.committing = false))
        return refreshed
    }

    async RefreshPublish() {
        try {
            // only the catalogue is refreshed here: InitAfterLoadSetting would also
            // overwrite the subscription config with undefined
            const response = await GetFeelGreatProduct(storeAuth.userStatus || null)
            storeFeelGreat.Init(response.data)
            return true
        } catch (error) {
            console.error('AddEditProduct publish refresh failed', error)
            return false
        }
    }

    FinishCommit(result) {
        runInAction(() => {
            this.committing = false
            this.commitResult = result
        })
        return result
    }

    IsPlaceOrderBlocked() {
        const addonsWithoutPack = StoreReferralCart.GetAddonItems().length > 0 && !StoreReferralCart.HasPack()
        return this.committing || this.syncError || addonsWithoutPack
    }

    CommitAccepted() {
        runInAction(() => {
            this.committedSnapshot = { ...this.draft }
            this.validationErrors = []
            this.addonsClearedByPackRemoval = false
            this.unavailableCodes = []
        })
    }

    ResetDraftState = () => {
        runInAction(() => {
            this.isOpen = false
            this.discardOpen = false
            this.committing = false
            this.syncError = false
            this.draft = {}
            this.committedSnapshot = {}
            this.draftProducts = {}
            this.unavailableCodes = []
            this.validationErrors = []
            this.addonsClearedByPackRemoval = false
            this.commitResult = null
        })
    }
}

const StoreAddEditProduct = new storeAddEditProduct()
export default StoreAddEditProduct
